import type { TLRichText } from "@tldraw/tlschema";
export type Mention = { userId: string; label: string; occurrenceId: string };
export function mentionsIn(body: unknown): Mention[] {
  const result: Mention[] = [];
  let nodes = 0;
  const visit = (value: unknown, depth: number) => {
    if (!value || typeof value !== "object" || depth > 30 || ++nodes > 20000)
      return;
    const n = value as {
      type?: string;
      attrs?: Record<string, unknown>;
      content?: unknown[];
    };
    if (n.type === "mention") {
      const a = n.attrs;
      if (
        !a ||
        typeof a.userId !== "string" ||
        !a.userId ||
        a.userId.length > 128 ||
        typeof a.label !== "string" ||
        a.label.length > 120 ||
        typeof a.occurrenceId !== "string" ||
        !/^[a-f0-9-]{36}$/.test(a.occurrenceId)
      )
        throw new Error("Invalid mention");
      result.push(a as Mention);
      if (result.length > 100 || new Set(result.map((m) => m.userId)).size > 20)
        throw new Error("Choose up to 20 project members");
    }
    if (Array.isArray(n.content))
      for (const c of n.content) visit(c, depth + 1);
  };
  visit(body, 0);
  return result;
}
/*
 * @Agent is its own inline node, never a member mention: member mentions are
 * checked against the project and drive notifications, while an agent mention
 * asks the caller's agent to work on what the comment is pinned to.
 */
export const AGENT_MENTION_LABEL = "Agent";
export function agentMentionsIn(body: unknown): string[] {
  const result: string[] = [];
  let nodes = 0;
  const visit = (value: unknown, depth: number) => {
    if (!value || typeof value !== "object" || depth > 30 || ++nodes > 20000)
      return;
    const n = value as {
      type?: string;
      attrs?: Record<string, unknown>;
      content?: unknown[];
    };
    if (n.type === "agentMention") {
      const id = n.attrs?.occurrenceId;
      if (typeof id !== "string" || !/^[a-f0-9-]{36}$/.test(id))
        throw new Error("Invalid agent mention");
      result.push(id);
      if (result.length > 20)
        throw new Error("Mention the agent up to 20 times");
    }
    if (Array.isArray(n.content))
      for (const c of n.content) visit(c, depth + 1);
  };
  visit(body, 0);
  return result;
}
export function mentionText(body: unknown): string {
  if (!body || typeof body !== "object") return "";
  const n = body as {
    type?: string;
    text?: string;
    attrs?: { label?: string };
    content?: unknown[];
  };
  if (n.type === "mention") return `@${n.attrs?.label ?? "Member"}`;
  if (n.type === "agentMention") return `@${AGENT_MENTION_LABEL}`;
  if (n.type === "hardBreak") return "\n";
  return (
    (n.text ?? "") +
    (n.content?.map(mentionText).join(n.type === "doc" ? "\n" : "") ?? "")
  );
}
export function stripMentions(body: TLRichText): TLRichText {
  type Node = {
    type?: string;
    text?: string;
    attrs?: { label?: string };
    content?: Node[];
    [key: string]: unknown;
  };
  const walk = (n: Node): Node =>
    n.type === "mention"
      ? { type: "text", text: `@${n.attrs?.label ?? "Member"}` }
      : n.type === "agentMention"
        ? { type: "text", text: `@${AGENT_MENTION_LABEL}` }
        : { ...n, ...(n.content ? { content: n.content.map(walk) } : {}) };
  return walk(body as Node) as TLRichText;
}
export function mentionQuery(text: string) {
  const match = /(?:^|[\s([{])@([^@\n]{0,80})$/.exec(text);
  return match ? { query: match[1], length: match[1].length + 1 } : null;
}
export function filterMentionPeople<T extends { id: string; name: string }>(
  people: T[],
  query: string,
) {
  const norm = (s: string) =>
    s.normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase();
  return [...people]
    .filter((p) => norm(p.name).includes(norm(query)))
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}
